
import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function CompanyInfoPage() {
  useEffect(() => {
    // SEO設定
    document.title = '会社情報 | おかやま建築設計事務所';
    
    // メタディスクリプション
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'おかやま建築設計事務所の詳細な会社情報。代表者紹介、沿革、組織体制、取得資格、受賞歴などの企業情報をご紹介いたします。');
    }

    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "おかやま建築設計事務所",
      "description": "岡山市・倉敷市を中心に、住宅・店舗・公共施設などの建築設計・監理を行う一級建築士事務所",
      "url": import.meta.env.VITE_SITE_URL,
      "telephone": "086-012-3456",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "表町1-2-3 岡山ビジネスタワー3階",
        "addressLocality": "岡山市北区",
        "postalCode": "700-0000",
        "addressCountry": "JP"
      },
      "founder": {
        "@type": "Person",
        "name": "岡山 太郎"
      },
      "foundingDate": "2015-04",
      "numberOfEmployees": 3
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                会社情報
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                おかやま建築設計事務所の詳細な企業情報をご紹介いたします
              </p>
            </div>
          </div>
        </section>

        {/* Company Profile */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Basic Information */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">基本情報</h2>
              <div className="bg-gray-50 rounded-xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">商号</dt>
                      <dd className="text-gray-700">おかやま建築設計事務所</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">英文商号</dt>
                      <dd className="text-gray-700">Okayama Architectural Design Office</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">代表者</dt>
                      <dd className="text-gray-700">代表取締役 岡山 太郎</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">設立</dt>
                      <dd className="text-gray-700">2015年4月1日</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">資本金</dt>
                      <dd className="text-gray-700">500万円</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">従業員数</dt>
                      <dd className="text-gray-700">3名（2024年12月現在）</dd>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">本社所在地</dt>
                      <dd className="text-gray-700">〒700-0000<br />岡山市北区表町1-2-3<br />岡山ビジネスタワー3階</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">電話番号</dt>
                      <dd className="text-gray-700">086-012-3456</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">FAX番号</dt>
                      <dd className="text-gray-700">086-012-3457</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">営業時間</dt>
                      <dd className="text-gray-700">9:00〜18:00</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">定休日</dt>
                      <dd className="text-gray-700">土曜・日曜・祝日</dd>
                    </div>
                    <div className="flex items-start">
                      <dt className="font-semibold text-gray-900 w-32 flex-shrink-0">事業年度</dt>
                      <dd className="text-gray-700">4月1日〜3月31日</dd>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Representative Profile */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">代表者紹介</h2>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="text-center">
                    <img
                      src="https://public.readdy.ai/ai/img_res/9dd266a04d6cc534f469fa2b09066497.jpg"
                      alt="代表取締役 岡山 太郎"
                      className="rounded-lg shadow-lg object-cover object-top w-full h-80 mx-auto mb-4"
                    />
                    <h3 className="text-xl font-bold text-gray-900">岡山 太郎</h3>
                    <p className="text-gray-600">代表取締役・一級建築士</p>
                  </div>
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">プロフィール</h4>
                      <p className="text-gray-700 leading-relaxed">
                        1980年岡山市生まれ。岡山大学工学部建築学科卒業後、東京の大手設計事務所にて10年間の実務経験を積む。
                        住宅から大規模商業施設まで幅広いプロジェクトに携わり、2015年に故郷岡山にて独立。
                        「地域に根ざした建築」をテーマに、機能性と美しさを両立した設計を手がけている。
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">保有資格</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 一級建築士（登録番号：第123456号）</li>
                        <li>• 建築士事務所登録（岡山県知事登録 第A-1234号）</li>
                        <li>• インテリアプランナー</li>
                        <li>• 宅地建物取引士</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">受賞歴</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li>• 2023年 岡山県建築文化賞 優秀賞</li>
                        <li>• 2022年 JIA中国支部建築賞 入選</li>
                        <li>• 2021年 グッドデザイン賞 受賞</li>
                        <li>• 2020年 岡山市景観賞 特別賞</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company History */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">沿革</h2>
              <div className="bg-gray-50 rounded-xl p-8">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2015年</span>
                    </div>
                    <div className="text-gray-700">
                      4月 おかやま建築設計事務所設立<br />
                      岡山市北区にて事業開始
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2016年</span>
                    </div>
                    <div className="text-gray-700">
                      初の住宅設計プロジェクト完成<br />
                      岡山県建築士事務所協会入会
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2018年</span>
                    </div>
                    <div className="text-gray-700">
                      商業施設設計事業開始<br />
                      従業員1名増員
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2020年</span>
                    </div>
                    <div className="text-gray-700">
                      公共建築設計事業開始<br />
                      岡山市景観賞 特別賞受賞
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2021年</span>
                    </div>
                    <div className="text-gray-700">
                      グッドデザイン賞受賞<br />
                      従業員1名増員（現在3名体制）
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2022年</span>
                    </div>
                    <div className="text-gray-700">
                      JIA中国支部建築賞入選<br />
                      リノベーション事業本格開始
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2023年</span>
                    </div>
                    <div className="text-gray-700">
                      岡山県建築文化賞 優秀賞受賞<br />
                      累計設計実績50件達成
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-20 flex-shrink-0">
                      <span className="font-bold text-blue-900">2024年</span>
                    </div>
                    <div className="text-gray-700">
                      創立10周年に向けた事業拡大<br />
                      持続可能な建築設計への取り組み強化
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Organization Chart */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">組織体制</h2>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-blue-50 rounded-lg">
                    <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-user-star-line text-white text-2xl"></i>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">代表取締役</h3>
                    <p className="text-gray-600 text-sm mb-2">岡山 太郎</p>
                    <p className="text-gray-500 text-xs">一級建築士<br />経営・設計統括</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-lg">
                    <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-pencil-ruler-2-line text-white text-2xl"></i>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">設計部</h3>
                    <p className="text-gray-600 text-sm mb-2">田中 花子</p>
                    <p className="text-gray-500 text-xs">二級建築士<br />設計・監理担当</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-lg">
                    <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-customer-service-2-line text-white text-2xl"></i>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">営業・事務</h3>
                    <p className="text-gray-600 text-sm mb-2">佐藤 次郎</p>
                    <p className="text-gray-500 text-xs">営業・事務<br />顧客対応担当</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Licenses and Certifications */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">取得資格・許可</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">建築関連資格</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      一級建築士（登録番号：第123456号）
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      二級建築士（登録番号：第234567号）
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      建築士事務所登録（岡山県知事登録 第A-1234号）
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      インテリアプランナー
                    </li>
                  </ul>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">その他資格</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      宅地建物取引士
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      福祉住環境コーディネーター2級
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      建築CAD検定2級
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-900 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      建築積算士補
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Awards and Recognition */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">受賞歴・表彰</h2>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-award-line text-white text-sm"></i>
                        </div>
                        <span className="font-semibold text-gray-900">2023年</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">岡山県建築文化賞 優秀賞</h4>
                      <p className="text-gray-600 text-sm">「倉敷の家」プロジェクトにて受賞</p>
                    </div>
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-award-line text-white text-sm"></i>
                        </div>
                        <span className="font-semibold text-gray-900">2022年</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">JIA中国支部建築賞 入選</h4>
                      <p className="text-gray-600 text-sm">「岡山コミュニティセンター」プロジェクトにて入選</p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-award-line text-white text-sm"></i>
                        </div>
                        <span className="font-semibold text-gray-900">2021年</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">グッドデザイン賞</h4>
                      <p className="text-gray-600 text-sm">「玉野の住宅」プロジェクトにて受賞</p>
                    </div>
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                          <i className="ri-award-line text-white text-sm"></i>
                        </div>
                        <span className="font-semibold text-gray-900">2020年</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">岡山市景観賞 特別賞</h4>
                      <p className="text-gray-600 text-sm">「表町商業ビル」プロジェクトにて受賞</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Affiliations */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">所属団体・協会</h2>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8 max-w-3xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>公益社団法人 日本建築家協会（JIA）</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>一般社団法人 岡山県建築士事務所協会</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>岡山市建築士会</span>
                    </li>
                  </ul>
                  <ul className="space-y-4 text-gray-700">
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>一般社団法人 岡山県建築士会</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>岡山商工会議所</span>
                    </li>
                    <li className="flex items-center">
                      <div className="w-3 h-3 bg-blue-900 rounded-full mr-4"></div>
                      <span>岡山県中小企業家同友会</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
